import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import {
  FirmwareUpdateDetails,
  getDeviceFirmwareUpdate,
  requestFirmwareUpdate,
} from "src/services/alen/firmwareUpdate";
import { AuthContext } from "src/contexts/AuthContext";
import { AlenDevice } from "src/interfaces/IAlenDevice";
import { AlenFirmwareUpdateStatus } from "./types";

export type FirmwareUpdateProgress = {
  device: AlenDevice;
  status: AlenFirmwareUpdateStatus;
};

export type DeviceWithFirmwareUpdateStatus = {
  device: AlenDevice;
  firmware: FirmwareUpdateDetails | null;
  error: boolean;
};

type FirmwareUpdateContextType = {
  checkFirmwareUpdateStatus: (device: AlenDevice) => void;
  setSelectedDeviceFirmware: React.Dispatch<
    React.SetStateAction<DeviceWithFirmwareUpdateStatus | null>
  >;
  selectedDeviceFirmware: DeviceWithFirmwareUpdateStatus | null;
  updateProgress: FirmwareUpdateProgress | null;
  setUpdateProgress: React.Dispatch<
    React.SetStateAction<FirmwareUpdateProgress | null>
  >;
  startFirmwareUpdate: () => void;
};

const FirmwareUpdateContext = createContext<
  FirmwareUpdateContextType | undefined
>(undefined);

const FirmwareUpdateContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [updateProgress, setUpdateProgress] =
    useState<FirmwareUpdateProgress | null>(null);
  const { user } = useContext(AuthContext);

  const [selectedDeviceFirmware, setSelectedDeviceFirmware] =
    useState<DeviceWithFirmwareUpdateStatus | null>(null);

  const checkFirmwareUpdateStatus = useCallback(
    (device: AlenDevice) => {
      if (!user?.alen) {
        return;
      }
      setSelectedDeviceFirmware({
        device,
        firmware: null,
        error: false,
      });
      getDeviceFirmwareUpdate(user.alen, device.id)
        .then((status) => {
          const mcuModule = status.result.find(
            (module) => module.type_desc === "MCU Module"
          );
          if (mcuModule === undefined) return;
          setSelectedDeviceFirmware({
            device,
            firmware: mcuModule,
            error: false,
          });
        })
        .catch(() => {
          setSelectedDeviceFirmware({
            device,
            firmware: null,
            error: true,
          });
        });
    },
    [user]
  );

  const startFirmwareUpdate = useCallback(async () => {
    if (!user?.alen) {
      return;
    }
    if (!selectedDeviceFirmware) {
      return;
    }
    const { device, firmware } = selectedDeviceFirmware;
    if (!firmware) {
      return;
    }
    await requestFirmwareUpdate(user.alen, device.id, firmware.channel);
    setUpdateProgress(() => ({
      device,
      status: AlenFirmwareUpdateStatus.UpdateInProgress,
    }));
  }, [selectedDeviceFirmware, user]);

  const value = useMemo(
    () => ({
      checkFirmwareUpdateStatus,
      setSelectedDeviceFirmware,
      selectedDeviceFirmware,
      updateProgress,
      setUpdateProgress,
      startFirmwareUpdate,
    }),
    [
      checkFirmwareUpdateStatus,
      selectedDeviceFirmware,
      setSelectedDeviceFirmware,
      updateProgress,
      setUpdateProgress,
      startFirmwareUpdate,
    ]
  );

  return (
    <FirmwareUpdateContext.Provider value={value}>
      {children}
    </FirmwareUpdateContext.Provider>
  );
};

const useFirmwareUpdateContext = () => {
  const context = React.useContext(FirmwareUpdateContext);
  if (context === undefined) {
    throw new Error(
      "useFirmwareUpdateContext must be used within a FirmwareUpdateContextProvider"
    );
  }
  return context;
};

export { FirmwareUpdateContextProvider };
export default useFirmwareUpdateContext;
